/** Literal hex on purpose: these are not variance states and must not borrow that scale. */

/** Spelled out so the SVG does not depend on Tailwind emitting fill utilities. */
const INK = "#0F1D21"
const MUTED = "#878E90"
const LINE = "#CFD2D3"
const NEUTRAL_FILL = "#F3F4F4"
const SAMSARA_EDGE = "#147A90"
const SAMSARA_FILL = "#E8F7FA"
const SAMSARA_INK = "#0B4652"
const DRIVER_EDGE = "#BF8E2A"
const DRIVER_FILL = "#FFF2D7"
const DRIVER_INK = "#5B4414"
const ALARM_INK = "#7A0E0E"

const STAGE_LABEL = {
  fontSize: 9.5,
  fill: MUTED,
  letterSpacing: 0.6,
  textTransform: "uppercase",
} as const

/** Each stored column has exactly one writer; the feeds are combined only on read. */
export function SyncFlowDiagram() {
  return (
    <div className="overflow-x-auto">
      <svg
        viewBox="0 0 880 252"
        role="img"
        aria-labelledby="sync-flow-title sync-flow-desc"
        className="h-auto w-full min-w-[680px]"
      >
        <title id="sync-flow-title">How Samsara and Track-POD data reaches the screens</title>
        <desc id="sync-flow-desc">
          Samsara writes the actual arrival, departure and status columns, and separately the
          estimated arrival and departure. Track-POD writes only its own trackpod-prefixed columns.
          Neither writes the other&rsquo;s. The two are combined every time a monitoring screen
          reads a stop, and never written back.
        </desc>

        <defs>
          <marker
            id="sync-flow-arrow"
            viewBox="0 0 8 8"
            refX="7"
            refY="4"
            markerWidth="6"
            markerHeight="6"
            orient="auto-start-reverse"
          >
            <path d="M 0 0.5 L 7.5 4 L 0 7.5 z" fill={LINE} />
          </marker>
        </defs>

        <text x="4" y="12" {...STAGE_LABEL}>
          REPORTED BY
        </text>
        <text x="164" y="12" {...STAGE_LABEL}>
          STORED — ONE WRITER EACH
        </text>
        <text x="436" y="12" {...STAGE_LABEL}>
          ON READ
        </text>
        <text x="612" y="12" {...STAGE_LABEL}>
          SHOWN
        </text>

        <g>
          <rect
            x="4"
            y="44"
            width="124"
            height="58"
            rx="6"
            fill={SAMSARA_FILL}
            stroke={SAMSARA_EDGE}
          />
          <text
            x="66"
            y="66"
            textAnchor="middle"
            fontSize="12.5"
            fontWeight="600"
            fill={SAMSARA_INK}
          >
            Samsara
          </text>
          <text x="66" y="81" textAnchor="middle" fontSize="10" fill={SAMSARA_INK}>
            vehicle GPS
          </text>
          <text x="66" y="93" textAnchor="middle" fontSize="10" fill={SAMSARA_INK}>
            geofence crossings
          </text>

          <rect
            x="4"
            y="168"
            width="124"
            height="58"
            rx="6"
            fill={DRIVER_FILL}
            stroke={DRIVER_EDGE}
          />
          <text
            x="66"
            y="190"
            textAnchor="middle"
            fontSize="12.5"
            fontWeight="600"
            fill={DRIVER_INK}
          >
            Track-POD
          </text>
          <text x="66" y="205" textAnchor="middle" fontSize="10" fill={DRIVER_INK}>
            driver&rsquo;s phone
          </text>
          <text x="66" y="217" textAnchor="middle" fontSize="10" fill={DRIVER_INK}>
            typed by hand
          </text>
        </g>

        <g fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace" fontSize="10">
          <rect
            x="164"
            y="30"
            width="240"
            height="52"
            rx="6"
            fill={NEUTRAL_FILL}
            stroke={SAMSARA_EDGE}
          />
          <text x="284" y="48" textAnchor="middle" fill={INK}>
            actual_arrival · actual_departure
          </text>
          <text x="284" y="63" textAnchor="middle" fill={INK}>
            status
          </text>
          <text x="284" y="76" textAnchor="middle" fontFamily="inherit" fontSize="9" fill={MUTED}>
            measured
          </text>

          <rect
            x="164"
            y="98"
            width="240"
            height="52"
            rx="6"
            fill={NEUTRAL_FILL}
            stroke={SAMSARA_EDGE}
            strokeDasharray="4 3"
          />
          <text x="284" y="116" textAnchor="middle" fill={INK}>
            estimated_arrival
          </text>
          <text x="284" y="131" textAnchor="middle" fill={INK}>
            estimated_departure
          </text>
          <text x="284" y="144" textAnchor="middle" fontFamily="inherit" fontSize="9" fill={MUTED}>
            predicted — see the second diagram
          </text>

          <rect
            x="164"
            y="166"
            width="240"
            height="62"
            rx="6"
            fill={NEUTRAL_FILL}
            stroke={DRIVER_EDGE}
          />
          <text x="284" y="184" textAnchor="middle" fill={INK}>
            trackpod_arrival
          </text>
          <text x="284" y="199" textAnchor="middle" fill={INK}>
            trackpod_departure · trackpod_status
          </text>
          <text x="284" y="215" textAnchor="middle" fontFamily="inherit" fontSize="9" fill={MUTED}>
            enrichment only, never the columns above
          </text>
        </g>

        <g stroke={LINE} strokeWidth="1.5" fill="none" markerEnd="url(#sync-flow-arrow)">
          <path d="M 128 66 C 146 66 146 56 162 56" />
          <path d="M 128 84 C 146 84 146 124 162 124" />
          <path d="M 128 197 L 162 197" />
        </g>

        <rect x="436" y="30" width="140" height="198" rx="6" fill="#FFFFFF" stroke={MUTED} />
        <text x="506" y="106" textAnchor="middle" fontSize="12.5" fontWeight="600" fill={INK}>
          Combined
        </text>
        <text x="506" y="124" textAnchor="middle" fontSize="10" fill={INK}>
          Samsara wins where
        </text>
        <text x="506" y="136" textAnchor="middle" fontSize="10" fill={INK}>
          it has an opinion;
        </text>
        <text x="506" y="148" textAnchor="middle" fontSize="10" fill={INK}>
          the driver fills its
        </text>
        <text x="506" y="160" textAnchor="middle" fontSize="10" fill={INK}>
          silences
        </text>
        <text
          x="506"
          y="182"
          textAnchor="middle"
          fontSize="9"
          fill={MUTED}
          fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
        >
          stopTiming.ts
        </text>

        <g stroke={LINE} strokeWidth="1.5" fill="none" markerEnd="url(#sync-flow-arrow)">
          <path d="M 404 56 C 420 56 420 100 434 100" />
          <path d="M 404 124 L 434 124" />
          <path d="M 404 197 C 420 197 420 152 434 152" />
        </g>

        <rect x="612" y="90" width="264" height="78" rx="6" fill="#FFFFFF" stroke={LINE} />
        <text x="744" y="114" textAnchor="middle" fontSize="12.5" fontWeight="600" fill={INK}>
          What monitoring shows
        </text>
        <text x="744" y="132" textAnchor="middle" fontSize="10" fill={INK}>
          Timeline · Map · Turnarounds
        </text>
        <text x="744" y="146" textAnchor="middle" fontSize="10" fill={INK}>
          Route and stop panels
        </text>
        <text x="744" y="160" textAnchor="middle" fontSize="9" fill={MUTED}>
          a driver-sourced time is marked with a phone icon
        </text>

        <g stroke={LINE} strokeWidth="1.5" fill="none" markerEnd="url(#sync-flow-arrow)">
          <path d="M 576 129 L 610 129" />
        </g>

        <text x="506" y="244" textAnchor="middle" fontSize="9" fill={MUTED}>
          resolved every read · nothing written back
        </text>
      </svg>
    </div>
  )
}

/** 10:00 is x=64, 11:00 is x=820. One minute is 12.6 units. */
const atMinute = (m: number) => 64 + m * 12.6

/** Worked example, not live data: the departure estimate is an arrival plus the planned window. */
export function StopTimesDiagram() {
  const rows = [
    { label: "Planned", y: 34 },
    { label: "Predicted", y: 74 },
    { label: "Actual", y: 114 },
  ]

  return (
    <div className="overflow-x-auto">
      <svg
        viewBox="0 0 880 196"
        role="img"
        aria-labelledby="stop-times-title stop-times-desc"
        className="h-auto w-full min-w-[680px]"
      >
        <title id="stop-times-title">
          A worked example: a stop planned 10:00 to 10:30, still being serviced at 10:55
        </title>
        <desc id="stop-times-desc">
          The stop was planned from 10:00 to 10:30. Samsara predicted arrival at 10:12, so the
          departure estimate became 10:42 — that prediction plus the same thirty-minute service
          window. The vehicle actually arrived at 10:18 and at 10:55 has not left. The departure
          estimate still reads 10:42, thirteen minutes in the past, because it is only recalculated
          while a fresh arrival prediction is still arriving.
        </desc>

        {rows.map((row) => (
          <text key={row.label} x="56" y={row.y + 14} textAnchor="end" fontSize="9.5" fill={MUTED}>
            {row.label}
          </text>
        ))}

        <rect
          x={atMinute(0)}
          y="34"
          width={atMinute(30) - atMinute(0)}
          height="20"
          rx="3"
          fill="#E7E8E9"
          stroke={LINE}
        />
        <text
          x={(atMinute(0) + atMinute(30)) / 2}
          y="48"
          textAnchor="middle"
          fontSize="10"
          fill={INK}
        >
          10:00 → 10:30 · 30 min on site
        </text>

        <rect
          x={atMinute(12)}
          y="74"
          width={atMinute(42) - atMinute(12)}
          height="20"
          rx="3"
          fill="#DCEDF6"
          stroke="#98B4CB"
          strokeDasharray="4 3"
        />
        <text
          x={(atMinute(12) + atMinute(42)) / 2}
          y="88"
          textAnchor="middle"
          fontSize="10"
          fill={INK}
        >
          ETA 10:12 → estimate 10:42
        </text>

        {/* Three sides only — the stop has no end yet. */}
        <path
          d={`M ${atMinute(55)} 114 L ${atMinute(18) + 3} 114 Q ${atMinute(18)} 114 ${atMinute(18)} 117 L ${atMinute(18)} 131 Q ${atMinute(18)} 134 ${atMinute(18) + 3} 134 L ${atMinute(55)} 134`}
          fill="#D2EFF5"
          stroke={SAMSARA_EDGE}
        />
        <text
          x={(atMinute(18) + atMinute(55)) / 2}
          y="128"
          textAnchor="middle"
          fontSize="10"
          fill={SAMSARA_INK}
        >
          arrived 10:18 · still on site
        </text>

        <line
          x1={atMinute(42)}
          y1="94"
          x2={atMinute(42)}
          y2="150"
          stroke={ALARM_INK}
          strokeWidth="1"
          strokeDasharray="2 2"
        />
        <text x={atMinute(42) - 6} y="70" textAnchor="end" fontSize="9.5" fill={ALARM_INK}>
          estimate frozen here — 13 min ago ▸
        </text>

        <line
          x1={atMinute(55)}
          y1="26"
          x2={atMinute(55)}
          y2="150"
          stroke={INK}
          strokeWidth="1.5"
          strokeDasharray="3 2"
        />
        <text x={atMinute(55)} y="22" textAnchor="middle" fontSize="10" fontWeight="600" fill={INK}>
          now 10:55
        </text>

        <line x1={atMinute(0)} y1="160" x2={atMinute(60)} y2="160" stroke={LINE} />
        {[0, 15, 30, 45, 60].map((m) => (
          <g key={m}>
            <line x1={atMinute(m)} y1="160" x2={atMinute(m)} y2="165" stroke={LINE} />
            <text x={atMinute(m)} y="178" textAnchor="middle" fontSize="9.5" fill={MUTED}>
              {`${10 + Math.floor(m / 60)}:${String(m % 60).padStart(2, "0")}`}
            </text>
          </g>
        ))}
      </svg>
    </div>
  )
}
